import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import { IAuditLogSliceState } from "../../utils/auditLog.types";
import { fetchAuditLogs, fetchEntityTimeline, fetchAuditStats } from "./auditLogThunk";

/**
 * Audit Log slice initial state.
 */
const initialState: IAuditLogSliceState = {
    logs: [],
    timeline: [],
    stats: [],
    pagination: { total: 0, page: 1, limit: 20, pages: 0 },
    status: ApiStatus.IDLE,
    timelineStatus: ApiStatus.IDLE,
    errorMessage: '',
};

/**
 * Audit Log Slice.
 */
const auditLogSlice = createSlice({
    name: 'auditLog',
    initialState: initialState,
    reducers: {
        clearTimeline: (state) => {
            state.timeline = [];
            state.timelineStatus = ApiStatus.IDLE;
        },
        resetAuditLogState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            // Fetch audit logs
            .addCase(fetchAuditLogs.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchAuditLogs.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.logs = action?.payload?.logs || [];
                state.pagination = action?.payload?.pagination || initialState.pagination;
                state.errorMessage = '';
            })
            .addCase(fetchAuditLogs.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.logs = [];
                state.errorMessage = action.error.message || 'Failed to fetch audit logs';
                toast.error(action.error.message || 'Failed to fetch audit logs');
            })

            // Fetch entity timeline
            .addCase(fetchEntityTimeline.pending, (state) => {
                state.timelineStatus = ApiStatus.LOADING;
            })
            .addCase(fetchEntityTimeline.fulfilled, (state, action: any) => {
                state.timelineStatus = ApiStatus.SUCCESS;
                state.timeline = action?.payload?.data || [];
                state.errorMessage = '';
            })
            .addCase(fetchEntityTimeline.rejected, (state, action) => {
                state.timelineStatus = ApiStatus.FAILED;
                state.timeline = [];
                state.errorMessage = action.error.message || 'Failed to fetch timeline';
            })

            // Fetch audit stats
            .addCase(fetchAuditStats.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchAuditStats.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.stats = action?.payload?.data || [];
                state.errorMessage = '';
            })
            .addCase(fetchAuditStats.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to fetch audit stats';
            })
    }
});

export const { clearTimeline, resetAuditLogState } = auditLogSlice.actions;
export default auditLogSlice.reducer;
